import { useCallback, useContext, useMemo } from 'react';
import _ from 'lodash';
import { useKazoo } from '../use-kazoo';
import { CallParkingApi, ParkedCalls } from '../../services/phone-service/call-parking-api';
import { CallsApi } from '../../services/phone-service/calls-api';
import { gregorianToDate } from '../../utils/date-time';
import { CallParkingContext } from './provider';
import { usePhoneActions } from '../use-phone';
import { entityCollection } from '../../utils/collection';

const ParkFeatureCode = '3';
const UnparkFeatureCode = '5';
const ParkSlotStartNumber = 101;

function mapParkedCall(entries: ParkedCalls) {
    const parkedCalls = entries.map(entry => {
        const [slot, call] = Object.entries(entry)[0];
        return {
            slot,
            phoneNumber: call.cid_number,
            callerNameId: call.cid_name,
            kazooCallId: call.call_id
        };
    });

    return parkedCalls;
}

function findFreeSpot(parkedCallIds: string[]) {
    const busySlots = new Set(parkedCallIds);
    for (let slotNum = ParkSlotStartNumber; ; slotNum++) {
        const slot = String(slotNum);
        if (!busySlots.has(slot)) return slot;
    }
}

export default function useCallParking() {
    const context = useContext(CallParkingContext);
    const kazoo = useKazoo();
    const { call, transfer } = usePhoneActions();

    if (!context) throw new Error('useCallParking must be used within a CallParkingProvider');

    const { setParkedCalls } = context;

    const parkedCallIds = useMemo(() => Object.keys(context.parkedCalls), [context.parkedCalls]);

    const park = useCallback(
        async (callId: string) => {
            const spot = findFreeSpot(parkedCallIds);
            const parkNumber = '*' + ParkFeatureCode + spot;
            transfer({ id: callId, number: parkNumber });
        },
        [parkedCallIds, transfer]
    );

    const unpark = useCallback(
        async (spot: string) => {
            const unparkNumber = '*' + UnparkFeatureCode + spot;
            call(unparkNumber);
        },
        [call]
    );

    const refreshStartTime = useCallback(async () => {
        if (parkedCallIds.length === 0) {
            context.setStartTime({});
            return;
        }

        if (kazoo.userCredentials) {
            const api = new CallsApi();
            const resp = await api.getChannels(
                kazoo.userCredentials.kazooApiUrl,
                kazoo.userCredentials.kazooAccountId,
                kazoo.userCredentials.kazooUserToken
            );

            const startTimes = _(resp)
                .keyBy('uuid')
                // @ts-ignore
                .mapValues(c => gregorianToDate(c.timestamp).getTime())
                .value();

            context.setStartTime(startTimes);
        }
    }, [context, kazoo.userCredentials, parkedCallIds.length]);

    const refreshParkedCallsList = useCallback(async () => {
        if (kazoo.userCredentials) {
            const api = new CallParkingApi();
            const parkedCallEntries = await api.getParkedCalls(
                kazoo.userCredentials.kazooApiUrl,
                kazoo.userCredentials.kazooAccountId,
                kazoo.userCredentials.kazooUserToken
            );
            const calls = mapParkedCall(parkedCallEntries);
            const callsEntity = entityCollection(calls, c => c.slot);
            setParkedCalls(callsEntity.entities);
            refreshStartTime();
        }
    }, [setParkedCalls, kazoo.userCredentials, refreshStartTime]);

    return { ...context, park, unpark, refreshStartTime, refreshParkedCallsList, parkedCallIds };
}
